// shadcn/ui component: https://ui.shadcn.com/docs/components/radix/field
import React, { useMemo } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from 'src/component/ds/utils';
import { Label } from 'src/component/ds/ui/label';
import { Separator } from 'src/component/ds/ui/separator';

/*
 * Deviations from shadcn:
 * - no `responsive` orientation or `@container/field-group`: Tailwind 3 has no
 *   container queries without a plugin we don't load
 * - Tailwind 4 variants rewritten for 3: `nth-last-2:` and `has-data-[...]:`
 * - FieldLabel drops the checked-card `bg-primary/5` tints: our colours are bare
 *   CSS variables, which Tailwind 3 can't apply an opacity modifier to
 * - FieldLabel turns destructive itself: our Label sets its own text colour,
 *   so it doesn't inherit Field's data-[invalid=true] one
 * - no `leading-snug` on FieldLabel/FieldTitle: their line height comes from
 *   text-sm (20px, as in Figma), not 1.375
 */

function FieldSet ({ className, ...props }: React.ComponentProps<'fieldset'>) {
  return (
    <fieldset
      data-slot='field-set'
      className={cn(
        'ds-flex ds-flex-col ds-gap-6',
        'has-[>[data-slot=checkbox-group]]:ds-gap-3 has-[>[data-slot=radio-group]]:ds-gap-3',
        className
      )}
      {...props}
    />
  );
}

function FieldLegend ({
  className,
  variant = 'legend',
  ...props
}: React.ComponentProps<'legend'> & { variant?: 'legend' | 'label' }) {
  return (
    <legend
      data-slot='field-legend'
      data-variant={variant}
      className={cn(
        'ds-mb-3 ds-font-medium',
        'data-[variant=legend]:ds-text-base',
        'data-[variant=label]:ds-text-sm',
        className
      )}
      {...props}
    />
  );
}

function FieldGroup ({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='field-group'
      className={cn(
        'ds-group/field-group ds-flex ds-w-full ds-flex-col ds-gap-7 data-[slot=checkbox-group]:ds-gap-3 [&>[data-slot=field-group]]:ds-gap-4',
        className
      )}
      {...props}
    />
  );
}

const fieldVariants = cva(
  'ds-group/field ds-flex ds-w-full ds-gap-3 data-[invalid=true]:ds-text-destructive',
  {
    variants: {
      orientation: {
        vertical: ['ds-flex-col [&>*]:ds-w-full [&>.sr-only]:ds-w-auto'],
        horizontal: [
          'ds-flex-row ds-items-center',
          '[&>[data-slot=field-label]]:ds-flex-auto',
          'has-[>[data-slot=field-content]]:ds-items-start has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:ds-mt-px'
        ]
      }
    },
    defaultVariants: {
      orientation: 'vertical'
    }
  }
);

function Field ({
  className,
  orientation = 'vertical',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof fieldVariants>) {
  return (
    <div
      role='group'
      data-slot='field'
      data-orientation={orientation}
      className={cn(fieldVariants({ orientation }), className)}
      {...props}
    />
  );
}

function FieldContent ({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='field-content'
      className={cn(
        'ds-group/field-content ds-flex ds-flex-1 ds-flex-col ds-gap-1.5 ds-leading-snug',
        className
      )}
      {...props}
    />
  );
}

function FieldLabel ({
  className,
  ...props
}: React.ComponentProps<typeof Label>) {
  return (
    <Label
      data-slot='field-label'
      className={cn(
        'ds-group/field-label ds-peer/field-label ds-flex ds-w-fit ds-gap-2 group-data-[disabled=true]/field:ds-opacity-50',
        'group-data-[invalid=true]/field:ds-text-destructive',
        'has-[>[data-slot=field]]:ds-w-full has-[>[data-slot=field]]:ds-flex-col has-[>[data-slot=field]]:ds-rounded-md has-[>[data-slot=field]]:ds-border [&>*]:data-[slot=field]:ds-p-4',
        'has-[[data-state=checked]]:ds-border-primary',
        className
      )}
      {...props}
    />
  );
}

function FieldTitle ({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='field-label'
      className={cn(
        'ds-flex ds-w-fit ds-items-center ds-gap-2 ds-text-sm ds-font-medium group-data-[disabled=true]/field:ds-opacity-50',
        className
      )}
      {...props}
    />
  );
}

function FieldDescription ({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot='field-description'
      className={cn(
        'ds-text-sm ds-leading-normal ds-font-normal ds-text-muted-foreground group-has-[[data-orientation=horizontal]]/field:ds-text-balance',
        'last:ds-mt-0 [&:nth-last-child(2)]:-ds-mt-1 [[data-variant=legend]+&]:-ds-mt-1.5',
        '[&>a]:ds-underline [&>a]:ds-underline-offset-4 [&>a:hover]:ds-text-primary',
        className
      )}
      {...props}
    />
  );
}

function FieldSeparator ({
  children,
  className,
  ...props
}: React.ComponentProps<'div'> & {
  children?: React.ReactNode;
}) {
  return (
    <div
      data-slot='field-separator'
      data-content={!!children}
      className={cn(
        'ds-relative -ds-my-2 ds-h-5 ds-text-sm group-data-[variant=outline]/field-group:-ds-mb-2',
        className
      )}
      {...props}
    >
      <Separator className='ds-absolute ds-inset-0 ds-top-1/2' />
      {children && (
        <span
          className='ds-relative ds-mx-auto ds-block ds-w-fit ds-bg-background ds-px-2 ds-text-muted-foreground'
          data-slot='field-separator-content'
        >
          {children}
        </span>
      )}
    </div>
  );
}

function FieldError ({
  className,
  children,
  errors,
  ...props
}: React.ComponentProps<'div'> & {
  errors?: Array<{ message?: string } | undefined>;
}) {
  const content = useMemo(() => {
    if (children) {
      return children;
    }

    if (!errors?.length) {
      return null;
    }

    const uniqueErrors = [
      ...new Map(errors.map((error) => [error?.message, error])).values()
    ];

    if (uniqueErrors?.length == 1) {
      return uniqueErrors[0]?.message;
    }

    return (
      <ul className='ds-ml-4 ds-flex ds-list-disc ds-flex-col ds-gap-1'>
        {uniqueErrors.map(
          (error, index) =>
            error?.message && <li key={index}>{error.message}</li>
        )}
      </ul>
    );
  }, [children, errors]);

  if (!content) {
    return null;
  }

  return (
    <div
      role='alert'
      data-slot='field-error'
      className={cn('ds-text-sm ds-font-normal ds-text-destructive', className)}
      {...props}
    >
      {content}
    </div>
  );
}

export {
  Field,
  FieldLabel,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLegend,
  FieldSeparator,
  FieldSet,
  FieldContent,
  FieldTitle
};
